import React, { useState, useEffect } from 'react'
import { FaArrowLeft } from 'react-icons/fa'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  fetchPayoutDetail,
  fetchBalance,
  editPayoutOption,
  manualPayout,
  Payout,
  instantPayoutToCard
} from '../../store/reducers/payoutReducer'
import { fetchPaymentMethods } from '../../store/reducers/paymentReducer'
import type { RootState } from '../../store/store'
import './WithdrawalsPage.css'
import { fetchPayoutCards } from '../../store/reducers/paymentReducer'

type TabType = 'manual' | 'instant'

export default function WithdrawalsPage() {
  const dispatch = useAppDispatch()

  const { history, balance, status } = useAppSelector((s: RootState) => s.payout)

  const { cardList } = useAppSelector((s: RootState) => s.payment)
  const { payoutCards } = useAppSelector((s: RootState) => s.payment);
  const stripeConnAccId = useAppSelector(
    (s: RootState) => s.auth.profile?.stripeConnAccId
  )

  const [tab, setTab] = useState<TabType>('manual')

  useEffect(() => {
    dispatch(fetchBalance())
    dispatch(fetchPayoutDetail())
    dispatch(fetchPaymentMethods())
    dispatch(fetchPayoutCards())
  }, [dispatch])

  useEffect(() => {
    const type = tab === 'manual' ? 'manual' : 'automatic'
    dispatch(editPayoutOption({ type }))
      .unwrap()
      .then(() => {
        dispatch(fetchBalance())
        dispatch(fetchPayoutDetail())
      })
      .catch(console.error)
  }, [tab, dispatch])

  const filtered = history.filter(r =>
    tab === 'manual' ? !r.automatic : r.automatic
  )

  const handleRequestPayout = () => {
    let commission = 0;
    if (tab === 'manual') {
      dispatch(manualPayout())
        .unwrap()
        .then(() => {
          dispatch(fetchBalance())
          dispatch(fetchPayoutDetail())
        })
        .catch(err => alert(`Payout failed: ${err}`))
    } else {
      const defaultCard = payoutCards.find(card => card.isDefault)
      commission = 100;
      if (!defaultCard) {
        alert('No default card found. Please add and set a card as default.')
        return
      }

      if (!stripeConnAccId) {
        alert('Stripe account not connected.')
        return
      }

      const avail = parseFloat(balance?.availableAmount ?? '0')
      const amountInCents = Math.round((avail) * 100)

      if (amountInCents <= 0) {
        alert('Insufficient balance for instant payout.')
        return
      }

      dispatch(instantPayoutToCard({
        amount: amountInCents - commission,
        currency: 'usd',
        cardId: defaultCard.id,
        customerId: stripeConnAccId
      }))
        .unwrap()
        .then(() => {
          alert('Instant payout successful!')
          dispatch(fetchBalance())
          dispatch(fetchPayoutDetail())
        })
        .catch(err => alert(`Instant payout failed: ${err}`))
    }
  }

  return (
    <div className="withdrawals-container">
      <div className="withdrawals-card">
        <header className="withdrawals-header">
          <FaArrowLeft
            className="withdrawals-back-icon"
            onClick={() => window.history.back()}
          />
          <h2 className="withdrawals-title">Withdrawals</h2>
        </header>

        <div className="withdrawals-body">
          <div className="withdrawals-tabs">
            <div
              className="withdrawals-slider"
              style={{
                transform: tab === 'manual' ? 'translateX(0%)' : 'translateX(100%)'
              }}
            />
            <button
              className={tab === 'manual' ? 'active' : ''}
              onClick={() => setTab('manual')}
            >
              Manual
            </button>
            <button
              className={tab === 'instant' ? 'active' : ''}
              onClick={() => setTab('instant')}
            >
              Instant
            </button>
          </div>

          <div className="balance-card">
            <div className="balance-label">Available Balance</div>
            <div className="balance-amount">
              {status === 'loading' ? '…' : `$${balance?.availableAmount ?? '0'}`}
            </div>
          </div>

          <ul className="withdrawals-list">
            <li className="withdrawals-item header">
              <div className="withdrawals-col amount">Amount</div>
              <div className="withdrawals-col currency">Currency</div>
              <div className="withdrawals-col status">Status</div>
              <div className="withdrawals-col type">Type</div>
            </li>

            {filtered.length > 0 ? (
              filtered.map((r: Payout) => (
                <li key={r.id} className="withdrawals-item">
                  <div className="withdrawals-col amount">${r.amount}</div>
                  <div className="withdrawals-col currency">{r.currency}</div>
                  <div className="withdrawals-col status">{r.status}</div>
                  <div className="withdrawals-col type">
                    {r.automatic ? 'instant' : 'manual'}
                  </div>
                </li>
              ))
            ) : (
              <li className="withdrawals-empty">No data found</li>
            )}
          </ul>

          <button
            className="withdrawals-action-button"
            onClick={handleRequestPayout}
            disabled={status === 'loading'}
          >
            {tab === 'manual'
              ? 'Request Manual Payout'
              : 'Request Instant Payout'}
          </button>
        </div>
      </div>
    </div>
  )
}
